// Direct Supabase API wrapper using fetch (bypassing broken JS client)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY!;

interface SupabaseResponse<T> {
  data: T | null;
  error: { message: string } | null;
}

class SupabaseApi {
  private async getAuthToken(): Promise<string | null> {
    try {
      const { supabase } = await import('./supabase');
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<SupabaseResponse<T>> {
    try {
      // Get the user's JWT token for RLS
      const authToken = await this.getAuthToken();
      const authHeader = authToken ? `Bearer ${authToken}` : `Bearer ${SUPABASE_KEY}`;
      
      const response = await fetch(`${SUPABASE_URL}/rest/v1${endpoint}`, {
        ...options,
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization': authHeader,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
          ...options.headers,
        },
      });

      const responseText = await response.text();
      
      if (response.ok) {
        const data = responseText ? JSON.parse(responseText) : null;
        return { data, error: null };
      } else {
        return { data: null, error: { message: `HTTP ${response.status}: ${responseText}` } };
      }
    } catch (err: any) {
      return { data: null, error: { message: err.message } };
    }
  }

  async insertUserProfile(profile: any) {
    // Use upsert (PATCH with onConflict) to handle existing users
    return this.makeRequest('/users', {
      method: 'POST',
      headers: {
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify(profile),
    });
  }

  async insertTrainerProfile(profile: { user_id: string; country?: string; referral_source?: string }) {
    const { user_id, country, referral_source } = profile;
    return this.makeRequest(`/users?id=eq.${user_id}`, {
      method: 'PATCH',
      body: JSON.stringify({ country, referral_source }),
    });
  }

  async insertClientProfile(profile: any) {
    // Member profiles are now stored in users table with role='member'
    // Additional client data can be stored in a separate table later if needed
    return { data: null, error: null };
  }

  async getUserProfile(userId: string) {
    return this.makeRequest(`/users?id=eq.${userId}`);
  }

  async getUserProfileByEmail(email: string) {
    return this.makeRequest(`/users?email=eq.${encodeURIComponent(email)}`);
  }

  async updateUserProfile(userId: string, updates: any) {
    return this.makeRequest(`/users?id=eq.${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }
}

export const supabaseApi = new SupabaseApi(); 